import React, { useMemo } from "react"
import { Col, Row, Card, CardBody, Button } from "reactstrap"
import { toast } from "react-toastify"

import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

import { usePermissions } from "hooks/useRolePermission"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

const PermissionList = () => {
  document.title = "Permission List"

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: permissionResponse,
    isLoading,
    refetch: permissionRefetch,
    isRefetching: permissionRefetchLoading,
  } = usePermissions(queryParams)

  const handlePermissionListRefresh = () => {
    permissionRefetch()
    toast.info("Permission list refreshed.")
  }

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Name", accessorKey: "name" },
      { header: "Slug", accessorKey: "slug" },
      {
        header: "Module",
        accessorKey: "module_name",
        cell: ({ row }) => row.original.module?.name || "-",
      },
      { header: "Description", accessorKey: "description" },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
    ],
    []
  )

  const permissionsData = permissionResponse?.data?.data || []
  const paginationInfo = permissionResponse?.data

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Permission" breadcrumbItem="Permission List" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody className="border-bottom d-flex align-items-center">
                <h5 className="mb-0 card-title flex-grow-1">
                  Permission Lists
                </h5>
                <div className="flex-shrink-0">
                  <Button
                    color="light"
                    onClick={handlePermissionListRefresh}
                    className="me-1"
                  >
                    <i
                      className={`mdi mdi-refresh ${
                        permissionRefetchLoading ? "mdi-spin" : ""
                      }`}
                    />
                  </Button>
                  {hasActiveFilters && (
                    <Button
                      color="danger"
                      onClick={resetParams}
                      className="me-1"
                    >
                      <i className="mdi mdi-close" /> Reset Filters
                    </Button>
                  )}
                </div>
              </CardBody>

              <CardBody>
                <Row className="mb-3">
                  <Col sm={4}>
                    <GlobalInputSearch
                      value={params.search}
                      onChange={setSearch}
                    />
                  </Col>
                </Row>

                <TableContainer
                  columns={columns}
                  data={permissionsData}
                  isPagination
                  paginationInfo={paginationInfo}
                  onPageChange={setPage}
                  loading={isLoading || permissionRefetchLoading}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default PermissionList
